import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Users, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTeamStore } from '../store/useTeamStore'
import { countGraphemes, hasEnoughAlphanumeric, teamErrorText } from '../utils/teamFormat'
import { hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

const NAME_MIN = 3
const NAME_MAX = 24

/**
 * Создание банды. Название — 3–24 графемы (эмодзи считается за один символ),
 * минимум 3 буквы или цифры; уникальность бэкенд считает без регистра,
 * пробелов, знаков и эмодзи.
 */
const TeamCreateModal = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation()
  const { createTeam, isMutating } = useTeamStore()
  const [name, setName] = useState('')

  if (!isOpen) return null

  const length = countGraphemes(name)
  const isValid = length >= NAME_MIN && length <= NAME_MAX && hasEnoughAlphanumeric(name)

  const handleCreate = async () => {
    if (!isValid || isMutating) return

    const res = await createTeam(name.trim())
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-create')
      toast.success(t('team.created', 'Банда создана!'))
      setName('')
      onCreated?.(res.team)
      return
    }
    if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-black shadow-2xl">
        <button
          onClick={onClose}
          disabled={isMutating}
          className="absolute right-4 top-4 p-2 text-text-secondary transition-colors hover:text-white disabled:opacity-50"
          aria-label={t('common.close', 'Закрыть')}
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-accent-primary/15 p-3">
              <Users size={32} className="text-accent-primary" />
            </div>
          </div>

          <h3 className="mb-2 text-center text-xl font-bold not-italic text-white">
            {t('team.createTitle', 'Создать банду')}
          </h3>
          <p className="mb-5 text-center text-sm text-text-secondary">
            {t('team.createDescription', 'Придумай название — его увидят в каталоге. Позже можно поменять оформление, описание и настройки.')}
          </p>

          <label className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
            <span>{t('team.name', 'Название банды')}</span>
            <span className={`tabular-nums ${length > NAME_MAX ? 'text-red-400' : ''}`}>
              {length}/{NAME_MAX}
            </span>
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('team.namePlaceholder', 'Skinoz Team')}
            className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
          />

          {/* Подсказка о требовании бэкенда: минимум 3 буквы или цифры */}
          {length > 0 && !isValid && (
            <p className="mt-2 text-xs text-red-400">
              {length < NAME_MIN || length > NAME_MAX
                ? t('team.nameLengthHint', 'От 3 до 24 символов')
                : t('team.namePlainHint', 'Нужно минимум 3 буквы или цифры')}
            </p>
          )}

          <button
            onClick={handleCreate}
            disabled={!isValid || isMutating}
            className="gradient-border-cta animate-gradient-shift mt-5 flex w-full items-center justify-center rounded-xl p-4 font-bold text-dark-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_25px_50px_-12px_rgba(0,255,127,0.3)] transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {isMutating
              ? <Loader2 size={20} className="animate-spin" />
              : t('team.createSubmit', 'Создать банду')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TeamCreateModal
